import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { FlagFilled } from '@ant-design/icons'
import { Collapse, Table, Button, message, Tabs } from 'antd'
import Moment from 'react-moment'
import { getFlags, resolveFlag } from '../../flag-management.services'
import { toStringToUnderscore } from '../../flag-management.tools'
import FlagComments from './flag-comment.component'
import * as moment from 'moment'
import Text from 'antd/lib/typography/Text'
import Skeletons from '../../../../shared/skeletons'
import { HistoricalContext } from '../../../../refactory/modules/jobs/infrastructure/job-detail/historical.context'
import RetryRequest from '../../../../shared/retry-request'

const { Panel } = Collapse
const { TabPane } = Tabs

const PENDING_FLAGS = new Set(['pending', 'viewed', 'assigned'])
const FLAG_SLA_TIMEPARAM = 'minutes'

const Flags = (props) => {
  const { job } = props

  const { t, i18n } = useTranslation()
  const [flags, setFlags] = useState(undefined)
  const [wasError, setWasError] = useState(false)
  const [showFlagComments, setShowFlagComments] = useState(false)
  const [selectedFlag, setSelectedFlag] = useState(null)
  const [loading, setLoading] = useState({})
  const { getJobHistorical } = useContext(HistoricalContext)

  useEffect(() => {
    if (job) {
      listFlags(job.id)

      return () => {
        setFlags(undefined)
      }
    }
  }, [job])

  const COLUMNS = [
    {
      title: t('jobs.flags.table.date'),
      dataIndex: 'date',
      width: 150
    },
    {
      title: t('jobs.flags.table.description'),
      dataIndex: 'description',
      width: 150
    },
    {
      title: t('jobs.flags.table.comment'),
      dataIndex: 'comment',
      width: 150
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 150
    }
  ]

  const solveFlag = (flag) => {
    if (flag && flag.category && flag.category.categoryParams && flag.category.categoryParams[0].comment) {
      commentFlag(flag)
    } else {
      setLoading({ [flag.id]: true })
      resolveFlag(flag.id)
        .then(async (result) => {
          if (result) {
            message.success(t('jobs.flags.solved'))
            getJobHistorical(job.id)
            await listFlags(job.id)
            setLoading({ [flag.id]: false })
          } else {
            setLoading({ [flag.id]: false })
          }
        })
        .catch((_) => {
          setLoading({ [flag.id]: false })
        })
    }
  }

  const listFlags = async (jobId) => {
    try {
      const flagsFromServer = await getFlags(jobId)
      setFlags((_) => flagsFromServer ?? [])
    } catch (_) {
      setWasError(true)
    }
  }

  const handleFlagComments = () => {
    setShowFlagComments(!showFlagComments)
  }

  const commentFlag = (flag) => {
    setSelectedFlag(flag)
    handleFlagComments()
  }

  const commentText = (comment) => {
    if (!comment) return ' - '
    const cleanComment = comment.replace(/:/, '')
    return i18n.exists(`flags.comments.${cleanComment}`) ? t(`flags.comments.${cleanComment}`) : comment
  }

  const getFlagsByStatus = (status) => {
    return flags.filter((flag) => {
      if (status === 'resolved') {
        return !PENDING_FLAGS.has(flag.status)
      } else {
        return PENDING_FLAGS.has(flag.status)
      }
    })
  }

  const getFlagTimeTextType = (flag) => {
    const currentTime = moment().tz(props.tz)
    const flagTime = moment(flag.createdAt).tz(props.tz)

    if (
      PENDING_FLAGS.has(flag.status) &&
      flag.category &&
      flagTime.add(flag.category.categoryParams[0].slaTime, FLAG_SLA_TIMEPARAM).isBefore(currentTime)
    ) {
      return 'danger'
    }

    return ''
  }

  const handleRetryRequest = () => {
    setWasError(false)
    listFlags(job.id)
  }

  const printFlagsTable = (status) => {
    const filterFlags = getFlagsByStatus(status)
    return (
      <Table
        locale={{ emptyText: t('empty-text') }}
        columns={COLUMNS}
        scroll={{ y: 200 }}
        size='small'
        loading={false}
        pagination={false}
        dataSource={(() => {
          return filterFlags.map((flag) => {
            const actions = PENDING_FLAGS.has(flag.status) ? (
              <Button loading={loading[flag.id]} onClick={() => solveFlag(flag)}>
                {t('jobs.flags.table.solve')}
              </Button>
            ) : (
              ''
            )
            return {
              key: flag.id,
              date: (
                <React.Fragment>
                  <Text type={getFlagTimeTextType(flag)}>
                    <Moment fromNow fromNowDuring={2 * 24 * 60 * 60 * 1000} format={'l - LT'} tz={props.tz}>
                      {flag.createdAt}
                    </Moment>
                  </Text>
                </React.Fragment>
              ),
              description: flag.category ? t(`flags.${toStringToUnderscore(flag.category.name)}`) : '',
              comment: commentText(flag?.params?.comment),
              actions
            }
          })
        })()}
      />
    )
  }

  return flags === undefined && !wasError ? (
    <Skeletons mode='LINE' />
  ) : wasError ? (
    <RetryRequest handleReload={handleRetryRequest} />
  ) : flags.length > 0 ? (
    <Collapse className='flags-card' bordered={false} defaultActiveKey={['101']} expandIconPosition='right'>
      <Panel
        header={
          <div>
            <FlagFilled className='flag-red' />
            <b>{` ${t('jobs.flags.title')}`}</b>
            <div className='total-flags'>{getFlagsByStatus('pending').length}</div>
          </div>
        }
        key='101'
      >
        <Tabs defaultActiveKey='1'>
          <TabPane tab={t('jobs.flags.pending')} key='1'>
            {printFlagsTable('pending')}
          </TabPane>
          <TabPane tab={t('jobs.flags.resolved')} key='2'>
            {printFlagsTable('resolved')}
          </TabPane>
        </Tabs>
      </Panel>
      {showFlagComments && (
        <FlagComments
          {...props}
          listFlags={listFlags}
          visible={showFlagComments}
          handleFlagComments={handleFlagComments}
          flag={selectedFlag}
        />
      )}
    </Collapse>
  ) : null
}

export default Flags
